/**
 * facet-range-slider.js
 * Ersetzt Checkbox-Filter durch Schieberegler (Dual-Range-Slider).
 *
 * Datei: assets/facet-range-slider.js
 * Einbindung: Wird von facets.liquid nur geladen wenn nötig.
 *
 * Nutzt exakt die gleichen CSS-Klassen wie der Theme-eigene Preisfilter
 * (price-range): .facet-range-wrapper, .facet-range, .facet-range__min,
 * .facet-range__max, .facet-range__input, .facet-input-wrapper
 *
 * Funktionsweise:
 * 1. Findet alle <details data-range-slider> Elemente
 * 2. Liest die Checkbox-Werte aus (numerisch, sortiert)
 * 3. Ersetzt die Checkbox-Liste durch einen Dual-Range-Slider
 * 4. Bei Slider-Änderung: setzt die passenden Checkboxen auf checked
 *    und löst das Form-Submit aus (AJAX-Reload via FacetForm)
 */
(function () {
  'use strict';

  class FacetRangeSlider extends HTMLElement {
    constructor() {
      super();

      this.checkboxes = [];
      this.numericValues = [];
      this.minRange = null;
      this.maxRange = null;
      this.minInput = null;
      this.maxInput = null;
      this.rangeMax = 0;
      this.unit = this.getAttribute('data-unit') || '';
    }

    connectedCallback() {
      this.init();
    }

    init() {
      // Finde die versteckten Original-Checkboxen
      const container = this.closest('[data-filter]');
      if (!container) return;

      this.checkboxes = Array.from(
        container.querySelectorAll('input[type="checkbox"]')
      );
      if (this.checkboxes.length < 2) return;

      // Checkbox-Liste zuverlässig verstecken
      // (hidden-Attribut wird von .grid-Klasse überschrieben)
      this.checkboxes.forEach(cb => {
        const li = cb.closest('li');
        if (li) li.style.display = 'none';
      });
      const cbList = container.querySelector('ul');
      if (cbList && cbList !== this.closest('ul')) {
        cbList.style.display = 'none';
      }

      // Numerische Werte extrahieren und sortieren
      this.numericValues = this.checkboxes
        .map((cb) => parseInt(cb.value, 10))
        .filter((v) => !isNaN(v))
        .sort((a, b) => a - b);

      if (this.numericValues.length < 2) return;

      const minVal = this.numericValues[0];
      const maxVal = this.numericValues[this.numericValues.length - 1];
      this.rangeMax = maxVal;

      // Aktive Werte ermitteln (aktuell ausgewählte Checkboxen)
      const activeValues = this.checkboxes
        .filter((cb) => cb.checked)
        .map((cb) => parseInt(cb.value, 10))
        .filter((v) => !isNaN(v));

      const currentMin = activeValues.length > 0 ? Math.min(...activeValues) : minVal;
      const currentMax = activeValues.length > 0 ? Math.max(...activeValues) : maxVal;

      const lowerPct = ((currentMin - minVal) / (maxVal - minVal)) * 100;
      const higherPct = ((currentMax - minVal) / (maxVal - minVal)) * 100;

      // Gap und Padding wie price-range (collection.css: price-range { gap: 1.8rem })
      this.style.gap = '1.8rem';

      // HTML aufbauen — gleiche Struktur wie price-range
      this.innerHTML = `
        <span class="text-subtext">Max: ${this.formatValue(maxVal)}</span>
        <div class="facet-range-wrapper relative"
             style="--range-min: ${lowerPct}%; --range-max: ${higherPct}%;">
          <input class="facet-range facet-range__min w-full pointer-events-none"
                 type="range" min="${minVal}" max="${maxVal}" step="1"
                 value="${currentMin}">
          <input class="facet-range facet-range__max w-full pointer-events-none absolute"
                 type="range" min="${minVal}" max="${maxVal}" step="1"
                 value="${currentMax}">
        </div>
        <div class="facet-input-wrapper flex items-center gap-4">
          <div class="flex items-center flex-grow relative gap-2">
            <span class="text-subtext">Von</span>
            <input class="form-control facet-range__input" type="number"
                   inputmode="numeric" autocomplete="off"
                   placeholder="${minVal}" min="${minVal}" max="${currentMax}"
                   step="1" ${activeValues.length > 0 ? `value="${currentMin}"` : ''}>
          </div>
          <div class="flex items-center flex-grow relative gap-2">
            <span class="text-subtext">Bis</span>
            <input class="form-control facet-range__input" type="number"
                   inputmode="numeric" autocomplete="off"
                   placeholder="${maxVal}" min="${currentMin}" max="${maxVal}"
                   step="1" ${activeValues.length > 0 ? `value="${currentMax}"` : ''}>
          </div>
        </div>
      `;

      // Referenzen auf die neuen Inputs
      this.minRange = this.querySelector('.facet-range__min');
      this.maxRange = this.querySelector('.facet-range__max');
      const numberInputs = this.querySelectorAll('.facet-range__input');
      this.minInput = numberInputs[0];
      this.maxInput = numberInputs[1];

      // Event-Listener — gleiche Logik wie PriceRange
      this.minRange.addEventListener('input', this.onRangeMinInput.bind(this));
      this.maxRange.addEventListener('input', this.onRangeMaxInput.bind(this));
      this.minRange.addEventListener('change', this.onRangeMinChange.bind(this));
      this.maxRange.addEventListener('change', this.onRangeMaxChange.bind(this));
      this.minInput.addEventListener('change', this.onInputMinChange.bind(this));
      this.maxInput.addEventListener('change', this.onInputMaxChange.bind(this));
      this.minInput.addEventListener('focus', function () { this.select(); });
      this.maxInput.addEventListener('focus', function () { this.select(); });
    }

    formatValue(val) {
      return val.toLocaleString('de-DE') + (this.unit ? ' ' + this.unit : '');
    }

    updateGradient() {
      const wrapper = this.querySelector('.facet-range-wrapper');
      if (!wrapper) return;
      const min = parseInt(this.minRange.min);
      const max = parseInt(this.minRange.max);
      const range = max - min || 1;
      const minPct = ((parseInt(this.minRange.value) - min) / range) * 100;
      const maxPct = ((parseInt(this.maxRange.value) - min) / range) * 100;
      wrapper.style.setProperty('--range-min', minPct + '%');
      wrapper.style.setProperty('--range-max', maxPct + '%');
    }

    // --- Range Slider Events ---
    onRangeMinInput(e) {
      const val = Math.min(parseInt(e.target.value), parseInt(this.maxRange.value) - 1);
      e.target.value = val;
      this.minInput.value = val;
      this.updateGradient();
    }

    onRangeMaxInput(e) {
      const val = Math.max(parseInt(e.target.value), parseInt(this.minRange.value) + 1);
      e.target.value = val;
      this.maxInput.value = val;
      this.updateGradient();
    }

    onRangeMinChange(e) {
      e.stopPropagation();
      this.minInput.value = e.target.value;
      this.syncCheckboxes();
    }

    onRangeMaxChange(e) {
      e.stopPropagation();
      this.maxInput.value = e.target.value;
      this.syncCheckboxes();
    }

    // --- Number Input Events ---
    onInputMinChange(e) {
      e.preventDefault();
      const min = parseInt(this.minRange.min);
      const maxVal = parseInt(this.maxInput.value || this.maxRange.value);
      e.target.value = Math.max(Math.min(parseInt(e.target.value), maxVal - 1), min);
      this.minRange.value = e.target.value;
      this.updateGradient();
      this.syncCheckboxes();
    }

    onInputMaxChange(e) {
      e.preventDefault();
      const max = parseInt(this.maxRange.max);
      const minVal = parseInt(this.minInput.value || this.minRange.value);
      e.target.value = Math.min(Math.max(parseInt(e.target.value), minVal + 1), max);
      this.maxRange.value = e.target.value;
      this.updateGradient();
      this.syncCheckboxes();
    }

    // --- Checkbox-Synchronisation & Form-Submit ---
    syncCheckboxes() {
      const minVal = parseInt(this.minInput.value || this.minRange.min);
      const maxVal = parseInt(this.maxInput.value || this.maxRange.max);

      // Prüfen ob der volle Bereich ausgewählt ist → keine Checkboxen setzen
      const fullMin = parseInt(this.minRange.min);
      const fullMax = parseInt(this.maxRange.max);
      const isFullRange = minVal <= fullMin && maxVal >= fullMax;

      let changed = false;
      this.checkboxes.forEach((cb) => {
        const v = parseInt(cb.value, 10);
        const shouldCheck = !isFullRange && v >= minVal && v <= maxVal;
        if (cb.checked !== shouldCheck) {
          cb.checked = shouldCheck;
          changed = true;
        }
      });

      if (changed) {
        // Form-Change-Event auslösen → FacetForm AJAX-Reload
        const form = this.closest('form[is="facet-form"]');
        if (form) {
          form.dirty = true;
          form.dispatchEvent(new Event('submit', { cancelable: true }));
        }
      }
    }
  }

  customElements.define('facet-range-slider', FacetRangeSlider);

  // --- Initialisierung nach DOM-Updates (AJAX-Reload) ---
  // Nach jedem FacetForm-Reload müssen neue Slider initialisiert werden
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const sliders = node.querySelectorAll
          ? node.querySelectorAll('facet-range-slider')
          : [];
        // Custom Elements werden automatisch via connectedCallback initialisiert
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
